import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import Icon1 from "../icons/Icon.js";
import DropCell from "./DropCell.js";
import Spinner from "../loading/Spinner.js";
import SmallSpinner from "../loading/SmallSpinner.js";

const DropzoneImage = ({
  onDrop,
  files = [],
  maxFiles = Infinity,
  maxSize = 52428800,
  dropzoneStyles = "",
  instructionsText = "Glissez vos fichiers ici, ou cliquez pour sélectionner des fichiers",
  onDelete,
  loading,
  mandatory = false,
  only_zip = false,
  handleClickVariant,
}) => {
  const [loadingData, setLoadingData] = React.useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      setLoadingData(true);

      let newFiles = [];
      for (const file of acceptedFiles) {
        if (!["image/jpeg", "image/png"].includes(file.type)) {
          if (only_zip) {
            console.error(file.type);
            handleClickVariant(
              "Veuillez déposer un fichier au bon format",
              "error"
            );
            newFiles = [];
            break;
          } else {
            newFiles.push(file); // Add non-ZIP files directly
          }
        } else {
          newFiles.push(file); // Add non-ZIP files directly
        }
      }

      if (newFiles.length > 0) {
        onDrop([...files, ...newFiles]); // Append new files to existing ones
      }

      setLoadingData(false);
    },
    [onDrop, files]
  );

  const onDropRejected = useCallback(
    (rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        if (file.file.size > maxSize) {
          console.error(
            `Rejected file due to size: ${file.file.name}. Size: ${file.file.size} bytes. Max allowed size: ${maxSize} bytes.`
          );
        } else {
          console.warn(
            `Rejected file: ${file.file.name}, Type: ${file.file.type}`
          );
        }
      });
      const fileNames = rejectedFiles.map((file) => file.file.name);
      alert(`Invalid file types or sizes: ${fileNames.join(", ")}`);
    },
    [maxSize]
  );

  const [onHover, setOnHover] = React.useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    onDropRejected,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div className="flex flex-col items-center w-full  py-1  gap-4 ">
      <div
        {...getRootProps()}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
        className={`border-3 border-dashed  rounded-2xl p-4 w-full h-c250 flex items-center justify-center text-center cursor-pointer ${
          mandatory
            ? "border-red-500 bg-red-100"
            : isDragActive
              ? "border-blue-500 bg-blue-50"
              : onHover
                ? "bg-blue-50 border-shadow_25"
                : "border-primary bg-transparent"
        } ${dropzoneStyles}`}
      >
        <input {...getInputProps()} />
        {loadingData ? (
          <SmallSpinner loading={true} />
        ) : (
          <div className="flex flex-col items-center">
            {isDragActive ? (
              <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                <Icon1
                  icon_name="join"
                  size={50}
                  fill="rgba(80, 81, 255, 0.7)"
                />
                <div className="text-hx2 font-bold text-black w-c200">
                  Lâchez pour déposer
                </div>
              </div>
            ) : (
              (!loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1
                    icon_name="image"
                    size={50}
                    fill="rgba(80, 81, 255, 0.7)"
                  />
                  <div className="text-hx2 font-bold text-black w-c200">
                    Glissez et déposez vos images ici
                  </div>
                  <div className="text-h3  text-shadow_50 w-c200">
                    Ou <span className="underline">cliquez</span> pour
                    sélectionner des images
                  </div>
                </div>
              )) ||
              (loading && (
                <div className="flex flex-col items-center text-h3 gap-2 text-shadow_50">
                  <Icon1
                    icon_name="file"
                    size={50}
                    fill="rgba(80, 81, 255, 0.7)"
                  />
                  <div className="text-hx2 font-bold text-black w-c200">
                    Téléchargement en cours ...
                  </div>
                  <SmallSpinner loading={true} margin={0} size={30} />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Display the dropped files */}
      <div className="w-full rounded-lg flex flex-col h-fit  py-1 gap-2 overflow-y-auto max-h-c300 ">
        {files.length !== 0 ? (
          files.map((file, index) => (
            <div
              key={index}
              className="w-full flex-row flex justify-between items-center border-l-2 border-blue-400  pl-2"
            >
              <div className="flex flex-row gap-3 items-center">
                <img
                  src={file.url}
                  alt={file.name}
                  className="w-20 h-20 rounded-lg object-contain"
                />
                <div className="flex flex-col ">
                  <div className="text-h3 text-black">{file.name}</div>
                </div>
              </div>
              <div className="cursor-pointer" onClick={() => onDelete(file)}>
                <Icon1 icon_name="close" size={15} fill="black" />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-h2 text-shadow_50 h-full w-full min-h-10 justify-center flex flex-col">
            Pas d'image déposée
          </div>
        )}
      </div>
    </div>
  );
};

export default DropzoneImage;
